if (!customElements.get('press-mentions-carousel')) {
  class PressMentionsCarousel extends HTMLElement {
    connectedCallback() {
      const template = this.querySelector('template');
      this.pageEl = this.querySelector('.press-mentions__page');

      if (!template || !this.pageEl) return;

      this.allImgs = Array.from(template.content.querySelectorAll('img'));
      if (!this.allImgs.length) return;

      this.speed = parseInt(this.dataset.speed, 10) || 3000;
      this.perPageDesktop = parseInt(this.dataset.perPageDesktop, 10) || 5;
      this.perPageMobile = parseInt(this.dataset.perPageMobile, 10) || 4;

      for (let index = this.allImgs.length - 1; index > 0; index -= 1) {
        const randomIndex = Math.floor(Math.random() * (index + 1));
        [this.allImgs[index], this.allImgs[randomIndex]] = [this.allImgs[randomIndex], this.allImgs[index]];
      }

      this.lastIsDesktop = null;
      this.startIndex = 0;
      this.buildCarousel();

      this.onResize = () => {
        const isDesktop = window.innerWidth >= 750;
        if (isDesktop !== this.lastIsDesktop) {
          this.buildCarousel();
        }
      };

      window.addEventListener('resize', this.onResize);
    }

    buildCarousel() {
      clearInterval(this.interval);
      const isDesktop = window.innerWidth >= 750;
      this.lastIsDesktop = isDesktop;
      this.perPage = isDesktop ? this.perPageDesktop : this.perPageMobile;
      this.startIndex = 0;
      this.renderPage(this.getPage());

      if (this.allImgs.length <= this.perPage) return;

      this.interval = window.setInterval(() => this.next(), this.speed);
    }

    getPage() {
      const items = [];
      for (let index = 0; index < this.perPage; index += 1) {
        items.push(this.allImgs[(this.startIndex + index) % this.allImgs.length]);
      }
      return items;
    }

    renderPage(logos) {
      this.pageEl.innerHTML = '';
      logos.forEach((img) => {
        const wrapper = document.createElement('div');
        wrapper.className = 'press-mentions__logo-wrapper';
        wrapper.appendChild(img.cloneNode(true));
        this.pageEl.appendChild(wrapper);
      });
    }

    next() {
      this.startIndex = (this.startIndex + this.perPage) % this.allImgs.length;
      const nextPage = this.getPage();
      this.pageEl.classList.add('fade-out');

      window.setTimeout(() => {
        this.renderPage(nextPage);
        this.pageEl.classList.remove('fade-out');
      }, 400);
    }

    disconnectedCallback() {
      clearInterval(this.interval);
      if (this.onResize) {
        window.removeEventListener('resize', this.onResize);
      }
    }
  }

  customElements.define('press-mentions-carousel', PressMentionsCarousel);
}

if (!customElements.get('pdp-deferred-section')) {
  class PdpDeferredSection extends HTMLElement {
    loadPromise = null;

    connectedCallback() {
      if (this.dataset.loaded === 'true') return;

      if (!('IntersectionObserver' in window)) {
        this.renderSection();
        return;
      }

      this.observer = new IntersectionObserver(
        (entries, observer) => {
          if (!entries[0]?.isIntersecting) return;
          observer.unobserve(this);
          this.renderSection();
        },
        { rootMargin: this.dataset.rootMargin || '0px 0px 300px 0px' }
      );

      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    renderSection() {
      if (this.dataset.loaded === 'true' || this.loadPromise) return this.loadPromise;

      if (this.dataset.sectionId) {
        this.loadPromise = this.fetchSectionMarkup().finally(() => {
          this.loadPromise = null;
        });
        return this.loadPromise;
      }

      this.renderTemplateSection();
      return Promise.resolve();
    }

    renderTemplateSection() {
      if (this.dataset.loaded === 'true') return;

      const sectionTemplate = this.querySelector('template[data-section-template]');
      if (!sectionTemplate) {
        this.setAttribute('aria-busy', 'false');
        return;
      }

      this.replaceChildren(sectionTemplate.content.cloneNode(true));
      this.dataset.loaded = 'true';
      this.setAttribute('aria-busy', 'false');

      if (typeof window.initializeScrollAnimationTrigger === 'function') {
        window.initializeScrollAnimationTrigger(this);
      }
    }

    async fetchSectionMarkup() {
      const sectionId = this.dataset.sectionId;
      if (!sectionId) {
        this.renderTemplateSection();
        return;
      }

      try {
        const url = new URL(this.dataset.sectionUrl || window.Shopify?.routes?.root || '/', window.location.origin);
        url.searchParams.set('section_id', sectionId);

        const response = await fetch(url.toString(), { credentials: 'same-origin' });
        if (!response.ok) throw new Error(`Failed to load section ${sectionId}: ${response.status}`);

        const responseText = await response.text();
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const renderedSection = html.getElementById(`shopify-section-${sectionId}`);
        const currentSection = this.closest('.shopify-section');

        if (!renderedSection || !currentSection) {
          throw new Error(`Section ${sectionId} was not found in the render response.`);
        }

        currentSection.replaceWith(renderedSection);

        if (typeof window.initializeScrollAnimationTrigger === 'function') {
          window.initializeScrollAnimationTrigger(renderedSection);
        }
      } catch (error) {
        console.error('Failed to load deferred PDP section', error);
        this.renderTemplateSection();
      }
    }
  }

  customElements.define('pdp-deferred-section', PdpDeferredSection);
}
